import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps snippets/selling-plan-picker.liquid's radio/select/hidden-input
 * values in sync with whichever variant is currently selected.
 *
 * Recharge assigns a distinct numeric selling_plan id to each variant even
 * for "the same" plan — e.g. this product's annual plan has a different id
 * per size, only the monthly plan's id happens to be shared across all of
 * them. The picker is rendered server-side for whichever variant loaded the
 * page, and variant-picker.js only morphs the <variant-picker> element
 * itself on selection, so without this, switching size kept submitting the
 * ORIGINAL variant's now-invalid selling_plan id, which Shopify's
 * cart/add.js rejects with a 422.
 *
 * Matches old → new choices by selling plan group id (data-group-id), which
 * is stable across variants. A variant may not offer every group (e.g. 3XL
 * has no yearly plan): that choice is hidden and disabled, so it's never
 * submitted, and selection falls back to the first plan the variant does
 * offer.
 */
class SellingPlanPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {CustomEvent<{promise: Promise<{detail: {resource: {selling_plan_allocations?: Array<{selling_plan_id: number, selling_plan_group_id: string}>}}}>}>} event */
  #handleProductSelect = (event) => {
    /** @type {any} */ (event).promise
      .then((/** @type {any} */ { detail }) => {
        const allocations = detail?.resource?.selling_plan_allocations;
        if (!Array.isArray(allocations)) return;

        /** @type {Map<string, number>} */
        const idByGroup = new Map(
          allocations.map((allocation) => [String(allocation.selling_plan_group_id), allocation.selling_plan_id])
        );

        this.#syncChoices(this.querySelectorAll('input[type="radio"][data-group-id]'), idByGroup);
        this.#syncChoices(this.querySelectorAll('select option[data-group-id]'), idByGroup);
        this.#syncHiddenInput(allocations);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[selling-plan-picker] Event promise rejected:', error);
      });
  };

  /**
   * @param {NodeListOf<HTMLInputElement | HTMLOptionElement>} choices
   * @param {Map<string, number>} idByGroup
   */
  #syncChoices(choices, idByGroup) {
    let selectionLost = false;

    for (const choice of choices) {
      const newId = idByGroup.get(choice.dataset.groupId ?? '');
      const offered = newId != null;
      const isSelected = choice instanceof HTMLInputElement ? choice.checked : choice.selected;

      if (offered) choice.value = String(newId);
      choice.disabled = !offered;
      (choice instanceof HTMLInputElement ? choice.closest('label') : choice)?.toggleAttribute('hidden', !offered);

      if (!offered && isSelected) selectionLost = true;
    }

    if (!selectionLost) return;

    const fallback = Array.from(choices).find((choice) => !choice.disabled);
    if (fallback instanceof HTMLInputElement) fallback.checked = true;
    else if (fallback) fallback.selected = true;
  }

  /** @param {Array<{selling_plan_id: number}>} allocations */
  #syncHiddenInput(allocations) {
    const hiddenInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="hidden"][name]'));
    if (!hiddenInput || allocations.length !== 1) return;
    hiddenInput.value = String(allocations[0].selling_plan_id);
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
